import { msg } from 'gt-react';
import {
  createRoute,
  lazyRouteComponent,
  redirect,
} from '@tanstack/react-router';
import { z } from 'zod';
import { Action, Subject } from '@erp/authz';
import { DiscountElementType, ProductModule } from '@erp/shared-types';
import { DiscountGroupSortBySchema } from '@erp/rpc';
import { TagIcon } from '@phosphor-icons/react';
import { fetchEnhancedSalesOrderUiEnabled } from '@/api/org/config/useEnhancedSalesOrderUi';
import {
  Route as pathlessAuthenticatedRoute,
  requireModules,
  requirePermission,
} from '../route';

// Discounts v2 owns the unversioned path. Its page components intentionally
// stay in ../discounts-v2/ (folder not renamed to avoid churn); the legacy
// Discounts page components live beside this module and are served at
// /discounts-v1.
const DiscountsV2Page = lazyRouteComponent(
  () => import('../discounts-v2/DiscountsV2Page'),
  'DiscountsV2Page',
);

export const Route = createRoute({
  getParentRoute: () => pathlessAuthenticatedRoute,
  path: '/discounts',
  staticData: {
    app: {
      nav: {
        label: msg('Discounts', {
          context: 'sidebar nav label, Sales section',
        }),
        icon: <TagIcon className="h-4 w-4" weight="fill" />,
        group: 'Sales',
        order: 40,
      },
      access: {
        modulesAnyOf: [ProductModule.ORDER_MANAGEMENT],
        permissionsAnyOf: [
          { action: Action.Read, subject: Subject.DiscountV2 },
        ],
      },
    },
  },
  component: DiscountsV2Page,
  // View + filters + pagination live in the URL so a refresh / shared link
  // restores the full view (same pattern as the Product Catalog).
  // `sku`, `customerName` and the free-form `tab` are legacy v1 deep-link
  // params declared only so they survive the legacy-org redirect below (zod
  // strips undeclared keys before beforeLoad runs).
  validateSearch: z.object({
    view: z.enum(['groups', 'elements']).optional().catch(undefined),
    page: z.number().int().min(1).optional().catch(undefined),
    pageSize: z.number().int().min(1).max(100).optional().catch(undefined),
    search: z.string().optional().catch(undefined),
    customerId: z.string().optional().catch(undefined),
    createdBy: z.string().optional().catch(undefined),
    groupStatus: z.string().optional().catch(undefined),
    elementStatus: z
      .enum(['active', 'inactive', 'all'])
      .optional()
      .catch(undefined),
    discountType: z.nativeEnum(DiscountElementType).optional().catch(undefined),
    family: z.string().optional().catch(undefined),
    group: z.string().optional().catch(undefined),
    model: z.string().optional().catch(undefined),
    // Groups-table sort (server-side); absent = newest first.
    sortBy: DiscountGroupSortBySchema.optional().catch(undefined),
    sortDirection: z.enum(['asc', 'desc']).optional().catch(undefined),
    sku: z.string().optional().catch(undefined),
    customerName: z.string().optional().catch(undefined),
    tab: z.string().optional().catch(undefined),
  }),
  loader: () => ({
    crumb: msg('Discounts', { context: 'page breadcrumb' }),
  }),
  beforeLoad: async ({ context, search }) => {
    requireModules({
      context,
      oneOf: [ProductModule.ORDER_MANAGEMENT],
    });
    // TEMPORARY redirect — remove after all tenants migrate to OMS v3.
    // Legacy orgs keep the old pages: redirect BEFORE the permission check so
    // a user without DiscountV2 lands on /discounts-v1, not /unauthorized.
    if (!(await fetchEnhancedSalesOrderUiEnabled(context.queryClient))) {
      throw redirect({ to: '/discounts-v1', search });
    }
    await requirePermission({
      context,
      action: Action.Read,
      subject: Subject.DiscountV2,
    });
  },
});
